// src/pages/spot/components/LandmarkActionUI.js
// 랜드마크 카드 내 인라인 액션 UI 컴포넌트 - 슬림 버전 (기존 규격 유지)

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import LoadingModal from './LoadingModal';

const LandmarkActionUI = ({ landmarkId, landmarkState, onVisit, onClaim, onConfirmVisit, onAdVisit, adVisitLoading = false, fullWidth = false, isMyLandSlot = false }) => {
    const { t } = useTranslation();
    const { status, isMyLand, isEmpty, isPending, isLoading } = landmarkState;

    // 광고 보고 방문/빼앗기 핸들러
    // 🐛 [Bugfix 2026-07-09] 광고 시청~방문 처리 중 중복 클릭 방지 (adVisitLoading이 true면 무시)
    const handleAdAction = (e) => {
        e.stopPropagation();
        if (adVisitLoading) return;
        if (onAdVisit) {
            onAdVisit(landmarkId);
        } else {
            alert(t('landmark_ad_coming_soon'));
        }
    };

    // 🎯 [UX Fix] 로딩 중일 때는 로딩 팝업 표시 (확인 버튼 없이)
    if (isPending || isLoading) {
        return (
            <>
                <LoadingModal isOpen={true} message={t('landmark_visiting')} />
                <ActionButton disabled $fullWidth={fullWidth} style={{ opacity: 0.5 }}>...</ActionButton>
            </>
        );
    }

    // ✅ [Fix] isEmpty가 true이면 절대 "점유 중" 버튼을 표시하지 않음
    if (isEmpty) {
        // 빈 랜드마크는 방문 버튼만 표시
        return (
            <ButtonGroup>
                <AdButton onClick={handleAdAction} disabled={adVisitLoading}>
                    {adVisitLoading ? `📺 ${t('landmark_ad_in_progress')}` : `📺 ${t('landmark_ad_visit')}`}
                </AdButton>
                <ActionButton onClick={(e) => {
                    e.stopPropagation();
                    if (onConfirmVisit) {
                        onConfirmVisit(landmarkId, () => onVisit(landmarkId));
                    } else {
                        onVisit(landmarkId);
                    }
                }}>
                    {t('landmark_visit')} 🎫1
                </ActionButton>
            </ButtonGroup>
        );
    }

    // ✅ [Fix] isMyLand가 true이고 isEmpty가 false일 때만 "점유 중" 표시
    if (isMyLand && !isEmpty) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                marginTop: fullWidth ? '8px' : '0'
            }}>
                <ActionButton disabled $fullWidth={fullWidth} $mine>
                    {t('landmark_mine')}
                </ActionButton>
            </div>
        );
    }

    // My Land 슬롯: 타인은 빼앗기 불가 (버튼 숨김)
    if (isMyLandSlot && !isEmpty && !isMyLand) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                marginTop: fullWidth ? '8px' : '0',
                fontSize: '0.8rem',
                color: '#6B7280'
            }}>
                {t('my_land_cannot_capture')}
            </div>
        );
    }

    // 빈 랜드마크 또는 남의 땅
    const buttonText = isEmpty ? t('landmark_visit') : t('landmark_capture');
    const adButtonText = isEmpty ? t('landmark_ad_visit') : t('landmark_ad_capture');

    return (
        <ButtonGroup>
            <AdButton onClick={handleAdAction} disabled={adVisitLoading}>
                {adVisitLoading ? `📺 ${t('landmark_ad_in_progress')}` : `📺 ${adButtonText}`}
            </AdButton>
            <ActionButton onClick={(e) => {
                e.stopPropagation();
                // ✅ Phase 1: 확인 팝업을 통한 안전한 액션
                if (onConfirmVisit) {
                    onConfirmVisit(landmarkId, () => onVisit(landmarkId));
                } else {
                    onVisit(landmarkId);
                }
            }}>
                {buttonText} 🎫1
            </ActionButton>
        </ButtonGroup>
    );
};


const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const AdButton = styled.button`
  flex: 1;
  padding: 14px 12px;
  background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-family: 'Outfit', 'Noto Sans KR', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${props => props.$mine && props.$fullWidth ? '12px 16px' : '14px 12px'};
  background: ${props => props.$mine ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : (props.$active ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : 'linear-gradient(135deg, #6C5CE7 0%, #4834D4 100%)')};
  color: white;
  border: none;
  border-radius: ${props => props.$mine && props.$fullWidth ? '10px' : '12px'};
  font-family: 'Outfit', 'Noto Sans KR', sans-serif;
  font-size: ${props => props.$mine && props.$fullWidth ? '0.8rem' : '0.85rem'};
  font-weight: 700;
  cursor: ${props => props.$mine ? 'default' : 'pointer'};
  transition: all 0.2s;
  box-shadow: ${props => props.$mine ? '0 2px 8px rgba(16, 185, 129, 0.2)' : '0 4px 15px rgba(108, 92, 231, 0.3)'};
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${props => props.$mine && props.$fullWidth ? 'max-width: 200px; margin: 0 auto;' : ''}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #ccc;
    box-shadow: none;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    filter: brightness(1.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export default LandmarkActionUI;
